import React, { useState } from 'react';
import '../styles/DataGrid.css';

const DataGrid = ({ 
  columns, 
  data, 
  pageSize = 10,
  sortable = true,
  filterable = true,
  loading = false,
  noDataMessage = 'No data available'
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');

  // Handle sorting
  const requestSort = (key) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting
  const sortedData = React.useMemo(() => {
    if (sortConfig.key === null) return data;
    
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Apply filtering
  const filteredData = React.useMemo(() => {
    if (!filterText) return sortedData;
    
    return sortedData.filter(item => {
      return columns.some(column => {
        const value = item[column.field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(filterText.toLowerCase());
      });
    });
  }, [sortedData, filterText, columns]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const pageData = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, pageSize]);

  // Page navigation
  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const getSortingIcon = (field) => {
    if (!sortable) return null;
    
    if (sortConfig.key === field) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  return (
    <div className="data-grid-container">
      {filterable && (
        <div className="filter-container">
          <input
            type="text"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="filter-input"
          />
        </div>
      )}

      <div className="table-container">
        <table className="data-grid">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.field} 
                  onClick={() => requestSort(column.field)}
                  className={sortable ? 'sortable' : ''}
                >
                  {column.headerName}
                  {getSortingIcon(column.field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="loading-cell" style={{ textAlign: 'center', padding: '20px' }}>
                  <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '48px', height: '48px' }} />
                </td>
              </tr>
            ) : pageData.length > 0 ? (
              pageData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column) => (
                    <td key={column.field}>
                      {column.renderCell 
                        ? column.renderCell(row) 
                        : row[column.field]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data-cell">
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => goToPage(0)}
            disabled={currentPage === 0}
            className="pagination-button"
          >
            First
          </button>
          <button 
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="pagination-button"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button 
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="pagination-button"
          >
            Next
          </button>
          <button 
            onClick={() => goToPage(totalPages - 1)}
            disabled={currentPage === totalPages - 1}
            className="pagination-button"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default DataGrid;